// src/components/sessions/PromotionModal.js
import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
    List, ListItem, ListItemText, CircularProgress, IconButton, Checkbox,
    FormControlLabel, Divider, Tabs, Tab, Chip
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PeopleIcon from '@mui/icons-material/People';
import BoltIcon from '@mui/icons-material/Bolt';
import EmailPreviewModal from './EmailPreviewModal';

// Helper component for Tab Panels
const TabPanel = (props) => {
    const { children, value, index, ...other } = props;
    return (
        <div role="tabpanel" hidden={value !== index} {...other}>
            {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
        </div>
    );
};

const PromotionModal = ({
                            open, onClose, session, prospects, loading, onSend, sending, onSendBlast
                        }) => {
    const [selectedProspects, setSelectedProspects] = useState(new Set());
    const [previewModalOpen, setPreviewModalOpen] = useState(false);
    const [prospectToPreview, setProspectToPreview] = useState(null);
    const [activeSend, setActiveSend] = useState(null);
    const [tabValue, setTabValue] = useState(0);

    const [blastData, setBlastData] = useState({ count: 0, preview: [] });
    const [isBlastLoading, setIsBlastLoading] = useState(false);

    useEffect(() => {
        if (!open) {
            setTabValue(0);
            setSelectedProspects(new Set());
            setBlastData({ count: 0, preview: [] });
        }
    }, [open]);

    useEffect(() => {
        if (open && tabValue === 1 && blastData.count === 0) {
            const fetchBlastPreview = async () => {
                setIsBlastLoading(true);
                try {
                    const response = await fetch('https://api.admin.marsreel.com/api/blast-preview');
                    if (!response.ok) {
                        throw new Error(`HTTP error! Status: ${response.status}`);
                    }
                    const data = await response.json();
                    setBlastData(data);
                } catch (error) {
                    console.error("Failed to fetch blast preview:", error);
                } finally {
                    setIsBlastLoading(false);
                }
            };
            fetchBlastPreview();
        }
    }, [open, tabValue, blastData.count]);

    useEffect(() => {
        if (!sending) setActiveSend(null);
    }, [sending]);

    const handleTabChange = (event, newValue) => setTabValue(newValue);
    const handleToggleSelect = (id) => {
        const newSelection = new Set(selectedProspects);
        newSelection.has(id) ? newSelection.delete(id) : newSelection.add(id);
        setSelectedProspects(newSelection);
    };
    const handleToggleSelectAll = (e) => setSelectedProspects(e.target.checked ? new Set(prospects.map(p => p.id)) : new Set());
    const handleSendClick = (sendAll) => {
        setActiveSend(sendAll ? 'allProspects' : 'selected');
        onSend(sendAll ? prospects : prospects.filter(p => selectedProspects.has(p.id)));
    };
    const handleSendBlastClick = () => {
        setActiveSend('blast');
        onSendBlast();
    };
    const handlePreviewClick = (p) => {
        setProspectToPreview(p);
        setPreviewModalOpen(true);
    };

    // ✨ FIX: Updated color palette for better readability and consistency
    const primaryActionColor = '#10a37f'; // Primary green for main actions
    const hoverActionColor = '#0e8969';
    const blastActionColor = '#3ea6ff';   // A confident blue for the blast action
    const blastHoverColor = '#3593e6';    // A slightly darker blue for hover
    const userChipColor = '#10a37f';      // Green for established "Users"
    const leadChipColor = '#3ea6ff';      // Blue for "Leads"
    const darkBg = '#2a2a2e';
    const lightText = '#f9f9f9';
    const subtleText = '#8e8ea0';
    const borderColor = '#444';

    return (
        <>
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" PaperProps={{ sx: { backgroundColor: darkBg, color: lightText, borderRadius: '12px', border: `1px solid ${borderColor}` } }}>
                <DialogTitle sx={{ fontWeight: 'bold', borderBottom: `1px solid ${borderColor}` }}>
                    Launch Campaign for: {session?.name}
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    <Box sx={{ borderBottom: 1, borderColor: borderColor }}>
                        <Tabs value={tabValue} onChange={handleTabChange} aria-label="campaign type tabs" variant="fullWidth"
                              sx={{
                                  '& .MuiTabs-indicator': { backgroundColor: primaryActionColor },
                                  '& .MuiTab-root': { color: subtleText, textTransform: 'none', fontSize: '1rem' },
                                  '& .Mui-selected': { color: `${primaryActionColor} !important`, fontWeight: 'bold' },
                              }}>
                            <Tab icon={<PeopleIcon />} iconPosition="start" label="Targeted Lookalikes" />
                            <Tab icon={<BoltIcon />} iconPosition="start" label="General Blast" />
                        </Tabs>
                    </Box>
                    <Box sx={{ p: 3 }}>
                        {/* TAB 1: TARGETED LOOKALIKES */}
                        <TabPanel value={tabValue} index={0}>
                            {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}><CircularProgress sx={{ color: primaryActionColor }} /></Box> :
                                <>
                                    <Typography variant="h6" gutterBottom>{prospects.length} potential recipients found</Typography>
                                    <FormControlLabel control={<Checkbox onChange={handleToggleSelectAll} checked={prospects.length > 0 && selectedProspects.size === prospects.length} indeterminate={selectedProspects.size > 0 && selectedProspects.size < prospects.length} sx={{ color: subtleText, '&.Mui-checked': { color: primaryActionColor } }} />} label="Select All" />
                                    <List sx={{ maxHeight: '45vh', overflow: 'auto', '&::-webkit-scrollbar': { width: '8px' }, '&::-webkit-scrollbar-thumb': { backgroundColor: borderColor, borderRadius: '4px' } }}>
                                        {prospects.map((prospect) => (
                                            <ListItem key={prospect.id} dense sx={{ '&:hover': { backgroundColor: '#3a3a3e' }, borderRadius: '8px', my: 0.5 }}>
                                                <Checkbox edge="start" checked={selectedProspects.has(prospect.id)} onChange={() => handleToggleSelect(prospect.id)} sx={{ color: subtleText, '&.Mui-checked': { color: primaryActionColor } }} />
                                                <ListItemText primary={`${prospect.name} (${prospect.email})`} secondary={`Match Type: ${prospect.matchType}`} secondaryTypographyProps={{ color: subtleText }} />
                                                <IconButton edge="end" title="Preview Email" onClick={() => handlePreviewClick(prospect)} sx={{ color: subtleText, '&:hover': { color: lightText } }}><VisibilityIcon /></IconButton>
                                            </ListItem>
                                        ))}
                                    </List>
                                </>}
                        </TabPanel>

                        {/* TAB 2: GENERAL BLAST */}
                        <TabPanel value={tabValue} index={1}>
                            {isBlastLoading ? <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}><CircularProgress sx={{ color: primaryActionColor }} /></Box> :
                                <>
                                    <Typography variant="h6">This campaign will be sent to <strong>{blastData.count.toLocaleString()}</strong> total users and leads.</Typography>
                                    <Typography variant="body2" color={subtleText} sx={{ mb: 2 }}>This is a preview of the first 100 recipients. The final email will be sent to your entire user base.</Typography>
                                    <List sx={{ maxHeight: '45vh', overflow: 'auto', '&::-webkit-scrollbar': { width: '8px' }, '&::-webkit-scrollbar-thumb': { backgroundColor: borderColor, borderRadius: '4px' } }}>
                                        {blastData.preview.map((recipient, index) => (
                                            <ListItem key={index} dense>
                                                {/* ✨ FIX: Explicitly set text colors for readability on dark background */}
                                                <ListItemText
                                                    primary={recipient.name}
                                                    secondary={recipient.email}
                                                    primaryTypographyProps={{ color: lightText }}
                                                    secondaryTypographyProps={{ color: subtleText }}
                                                />
                                                {/* ✨ FIX: Updated chip colors to be more intuitive */}
                                                <Chip
                                                    label={recipient.source}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: recipient.source === 'User' ? userChipColor : leadChipColor,
                                                        color: 'white',
                                                        fontWeight: 'bold'
                                                    }}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </>}
                        </TabPanel>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ borderTop: `1px solid ${borderColor}`, p: '16px 24px' }}>
                    <Button onClick={onClose} sx={{ color: lightText, textTransform: 'none' }} disabled={sending}>Cancel</Button>
                    <Box sx={{ flex: '1 1 auto' }} />

                    {tabValue === 0 && (
                        <>
                            <Button onClick={() => handleSendClick(false)} variant="outlined" disabled={loading || selectedProspects.size === 0 || sending} sx={{ color: primaryActionColor, borderColor: primaryActionColor, textTransform: 'none', borderRadius: '8px', '&:hover': { backgroundColor: 'rgba(16, 163, 127, 0.1)' } }}>
                                {sending && activeSend === 'selected' ? <CircularProgress size={20} sx={{ color: primaryActionColor, mr: 1 }} /> : null}
                                Send to {selectedProspects.size} Selected
                            </Button>
                            <Button onClick={() => handleSendClick(true)} variant="contained" disabled={loading || prospects.length === 0 || sending} sx={{ backgroundColor: primaryActionColor, '&:hover': { backgroundColor: hoverActionColor }, textTransform: 'none', borderRadius: '8px' }}>
                                {sending && activeSend === 'allProspects' ? <CircularProgress size={20} sx={{ color: 'white', mr: 1 }} /> : null}
                                Send to All {prospects.length}
                            </Button>
                        </>
                    )}

                    {tabValue === 1 && (
                        // ✨ FIX: Updated button color from red to blue
                        <Button onClick={handleSendBlastClick} variant="contained" disabled={isBlastLoading || blastData.count === 0 || sending} sx={{ backgroundColor: blastActionColor, '&:hover': { backgroundColor: blastHoverColor }, textTransform: 'none', borderRadius: '8px' }}>
                            {sending && activeSend === 'blast' ? <CircularProgress size={20} sx={{ color: 'white', mr: 1 }} /> : null}
                            Send Blast to All {blastData.count.toLocaleString()} Users
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            <EmailPreviewModal open={previewModalOpen} onClose={() => setPreviewModalOpen(false)} prospect={prospectToPreview} session={session} />
        </>
    );
};

export default PromotionModal;
